"use client";

import { Trophy, ActivitySquare, Network, Database } from "lucide-react";
import udataImage from "@/assets/images/udata.png";
import Image from "next/image";
import Section from "@/components/section";
import { Icons } from "@/components/icons";
import { useNextSectionScroll } from "@/lib/hooks/use-next-section-scroll";
import { BentoCard, BentoGrid } from "@/components/magicui/bento-grid";
import { AnimatedListDemo } from "@/components/magicui/animated-list";
import Marquee from "@/components/magicui/marquee";
import { AnimatedBeamDemo } from "@/components/magicui/n8n-beam";
import { useTranslations } from "@/lib/hooks/use-translations";

const iconMap = {
  0: Trophy,
  1: ActivitySquare,
  2: Network,
  3: Database
};

const backgroundMap = {
  0: (files: any[]) => (
    <Marquee
      pauseOnHover
      className="absolute top-2 [--duration:20s] [mask-image:linear-gradient(to_top,transparent_0%,#000_100%)] ml-[-32px]"
    >
      {files.map((f, idx) => (
        <figure
          key={idx}
          className="relative w-32 cursor-pointer overflow-hidden rounded-xl border p-4 border-border/5 bg-white hover:bg-white/80 dark:bg-white/5 dark:hover:bg-white/10 transform-gpu  transition-all duration-300 ease-out hover:blur-none"
        >
          <div className="flex flex-row items-center gap-2">
            <div className="flex flex-col">
              <figcaption className="text-sm font-medium text-foreground">
                {f.name}
              </figcaption>
            </div>
          </div>
          <blockquote className="mt-2 text-xs text-muted-foreground">{f.body}</blockquote>
        </figure>
      ))}
    </Marquee>
  ),
  1: () => (
    <AnimatedListDemo className="absolute right-2 top-[-2.5rem] h-[300px] w-full scale-75 border-none transition-all duration-300 ease-out [mask-image:linear-gradient(to_top,transparent_10%,#000_70%)] group-hover:scale-90" />
  ),
  2: () => (
    <AnimatedBeamDemo className="absolute inset-0 transition-all duration-300 ease-out [mask-image:linear-gradient(to_top,transparent_10%,#000_70%)] group-hover:scale-105" />
  ),
  3: () => (
    <div className="absolute inset-0">
      <Image
        src={udataImage}
        alt="Unified Data Background"
        fill
        className="object-cover opacity-40"
      />
    </div>
  )
};

const classMap = {
  0: "col-span-3 lg:col-span-1",
  1: "col-span-3 lg:col-span-2",
  2: "col-span-3 lg:col-span-2",
  3: "col-span-3 lg:col-span-1"
};

export default function Solution({ locale = "en" }: { locale?: "de" | "en" | "zh-CN" } = {}) {
  const handleNextSection = useNextSectionScroll("solution");
  const { t, locale: currentLocale } = useTranslations();

  const getContactUrl = () => {
    const localeToUse = currentLocale || locale;
    if (localeToUse === "de") return "/de/kontakt";
    if (localeToUse === "zh-CN") return "/zh-CN/contact";
    return "/contact";
  };

  const files = t<Array<{ name: string; body: string }>>('pages.home.solution.files');
  const features = t<Array<{ name: string; description: string }>>('pages.home.solution.features');

  const featureComponents = features.map((feature, idx) => ({
    Icon: iconMap[idx as keyof typeof iconMap],
    name: feature.name,
    description: feature.description,
    className: classMap[idx as keyof typeof classMap],
    background: backgroundMap[idx as keyof typeof backgroundMap](idx === 0 ? files : [])
  }));

  return (
    <Section
      id="solution"
      title={t('pages.home.solution.title')}
      subtitle={t('pages.home.solution.subtitle')}
      description={t('pages.home.solution.description')}
      titleBlockClassName="lg:pb-14"
      className="bg-neutral-100 dark:bg-neutral-900"
      mainCta={{
        text: t('pages.home.hero.cta.getStarted'),
        href: getContactUrl(),
        icon: <Icons.logoIcon className="h-6 w-6" />
      }}
      secondaryCta={{
        text: t('pages.home.hero.cta.readMore'),
        onClick: handleNextSection
      }}
    >
      <BentoGrid>
        {featureComponents.map((feature, idx) => (
          <BentoCard key={idx} {...feature} />
        ))}
      </BentoGrid>
    </Section>
  );
}
